import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import arrowDownIcon from "../../assets/images/arrow-down.svg";
import closeIcon from "../../assets/images/close.svg";
import logoSmallIcon from "../../assets/images/logo-symbol_2.svg";
import logoIcon from "../../assets/images/logotype_2.svg";
import useTranslations from "../../hooks/useTranslations";
import { ToolNavigationItem } from "../../models/navigation/ToolNavigationItem";
import { ToolNavigationItemType } from "../../models/navigation/ToolNavigationItemType";
import { SiteSettings } from "../../models/shared/SiteSettings";
import SanityLink from "../SanityLink";
import { HeaderTheme } from "./HeaderTheme";
import MarketNavigation from "./MarketNavigation";
import { createNavigation } from "./NavigationFactory";
import { NavigationSection } from "./NavigationSection";
import { PromotedLink } from "./PromotedLink";
import { GoToMainContentBtn } from "./Tools/GoToMainContentBtn";
import { LanguageToolLink } from "./Tools/LanguageToolLink";
import { ToolItem } from "./Tools/ToolItem";

const theme = {
  [HeaderTheme.Primary]: {
    tab: "navigation-tab--primary",
    tabAccent: "navigation-tab-accent--primary"
  },
  [HeaderTheme.Secondary]: {
    tab: "navigation-tab--secondary",
    tabAccent: "navigation-tab-accent--secondary"
  }
};

interface Props {
  siteSettings?: SiteSettings;
  isFixed?: boolean;
}

export const MarketHeader = ({ siteSettings, isFixed = false }: Props) => {
  const translations = useTranslations();

  const mainNavigation = useMemo(
    () => (siteSettings ? createNavigation(siteSettings) : []),
    [siteSettings]
  );

  const promotedLinks: PromotedLink[] =
    siteSettings?.promotedLinks?.map(link => ({
      title: link.title,
      description: link.description,
      linkReference: link.link,
      icon: link.icon
    })) ?? [];

  const [navigationState, selectItem] = useState<{
    selectedItemIndex: number | null;
    selectedItem: NavigationSection | null;
  }>();

  const [navigationColor, setNavigationColor] = useState<string>();
  const [isScrolled, setIsScrolled] = useState(false);

  const mobileLanguageSelector: ToolNavigationItem | undefined =
    siteSettings?.toolNavigation?.find(
      item => item.type === ToolNavigationItemType.Language && item.showAtTopInMobile
    );

  const mobileToolLinks =
    siteSettings?.toolNavigation?.filter(item => item !== mobileLanguageSelector) ?? [];

  const onItemSelected = (index: number) => {
    if (index === undefined || index === -1) {
      selectItem(undefined);
      return;
    }

    if (navigationState && index === navigationState.selectedItemIndex) {
      selectItem(undefined);
    } else {
      selectItem({
        selectedItemIndex: index,
        selectedItem: mainNavigation[index]
      });

      setNavigationColor(mainNavigation[index].color);
    }
  };

  const tabTheme = theme[navigationState?.selectedItem?.color ?? HeaderTheme.Primary];

  // Close on esc or click outside
  const closeCallback = () => selectItem(undefined);

  const onEscPressed = useCallback(
    (e: KeyboardEvent) => {
      if (!navigationState || navigationState.selectedItemIndex === null) {
        return;
      }

      if (e.key === "Escape") {
        closeCallback();
      }
    },
    [navigationState]
  );

  const onClickOutside = () => closeCallback();

  const onClickInside = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (typeof window === "undefined" || navigationState === null) {
      return;
    }

    window.addEventListener("keydown", onEscPressed, false);

    return () => {
      window.removeEventListener("keydown", onEscPressed, false);
    };
  }, [navigationState, onEscPressed]);

  useEffect(() => {
    const onBackgroundChange = () => {
      if (window.scrollY >= 80) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    if (isFixed) {
      window.addEventListener("scroll", onBackgroundChange);
      return () => {
        window.removeEventListener("scroll", onBackgroundChange);
      };
    }
  }, [isFixed]);

  return (
    <header
      className={classNames(
        "header-fixed-container h-12 xl:h-20",
        isFixed ? "fixed top-0 left-0" : ""
      )}
    >
      <div
        className={classNames("header header--solid h-12 xl:h-20", {
          "bg-transparent text-[var(--neutral-100)] shadow-none": isFixed && !isScrolled,
          "bg-[var(--neutral-100)] text-[var(--blue-20)] shadow-xl": isFixed && isScrolled
        })}
      >
        <GoToMainContentBtn title={translations.goToMainContent} />
        <div className="header-bar">
          <div className="header-main">
            <SanityLink
              className="header-logo-mobile focus:shadow-outline"
              aria-label={translations.home}
            >
              <svg width={30} height={24}>
                <use xlinkHref={logoSmallIcon.url} />
              </svg>
            </SanityLink>
            <SanityLink
              className="header-logo-desktop focus:shadow-outline"
              aria-label={translations.home}
            >
              <svg width={165} height={25}>
                <use xlinkHref={logoIcon.url} />
              </svg>
            </SanityLink>

            {!!mainNavigation && (
              <nav className="header-navigation justify-start xl:h-full">
                {mainNavigation.map((item, index) => {
                  const isOpen = navigationState?.selectedItemIndex === index;

                  return (
                    <button
                      key={index}
                      type="button"
                      className={`navigation-tab focus:outline-none ${
                        isOpen && navigationState?.selectedItem
                          ? tabTheme.tab
                          : "border-transparent focus:shadow-outline"
                      }`}
                      onClick={() => onItemSelected(index)}
                    >
                      <span className="navigation-tab-text">{item.title}</span>
                      <svg
                        width={12}
                        height={12}
                        className={`${
                          isOpen ? "fill-current" : "stroke-current"
                        } transition-none flex-none`}
                      >
                        <use xlinkHref={isOpen ? closeIcon.url : arrowDownIcon.url} />
                      </svg>
                      <span
                        className={`navigation-tab-accent ${
                          isOpen && navigationState?.selectedItem
                            ? tabTheme.tabAccent
                            : "bg-transparent"
                        }`}
                        aria-hidden
                      ></span>
                      {index !== 0 && (
                        <span
                          className="absolute top-0 left-0 h-full w-px bg-separator-subtle xl:hidden"
                          aria-hidden
                        ></span>
                      )}
                    </button>
                  );
                })}
                {mobileLanguageSelector && (
                  <div className="navigation-tab cursor-default xl:hidden">
                    <LanguageToolLink
                      fontClass="uppercase"
                      optionClassName="text-sm normal-case"
                      {...mobileLanguageSelector}
                    />
                  </div>
                )}
              </nav>
            )}
          </div>

          {!!siteSettings?.toolNavigation && (
            <div className="header-tools hidden xl:flex">
              {siteSettings.toolNavigation.map((item, index) => (
                <ToolItem
                  key={index}
                  {...item}
                  targetDevice="desktop"
                  showSeparator={index !== 0 && !item.isCallToAction}
                />
              ))}
            </div>
          )}
        </div>
        <MarketNavigation
          selectedTopLevel={navigationState?.selectedItemIndex}
          open={navigationState && navigationState.selectedItemIndex !== -1}
          color={navigationColor}
          mobileToolLinks={mobileToolLinks}
          navigationItems={mainNavigation}
          promotedLinks={promotedLinks}
          onClickOutside={onClickOutside}
          onClickInside={onClickInside}
        />
      </div>
    </header>
  );
};

